import {
  DatastoreObjectType,
  dbObjectMeta,
  MapLike,
  MayContainMetaData,
  Networks,
  Position,
  TypeScopedValidCoreApplicationType,
  ValidBusinessObject,
} from '@iotv/iotv-v3-types'
import { ValidModelObject } from '../../classDependentTypes'

import { ModelCommon } from '../ModelCommon'
import { DeviceState } from '../../ModelClassInterfaces'
import { enumerable } from '../constructs/enumerableDecorator'

const className: 'DeviceHistory' = 'DeviceHistory'

export enum LINK {
  LINK = 'Link',
  UNLINK = 'Unlink',
}

export interface DeviceInitialType {
  RSSI: number
  SNR: number
  battery?: number
  gateway: string
  timestamp: number
  settings?: string
  NS: Networks
}

export interface NetworkAS {
  provider: Networks
  timestamp: number
}

export interface LoctionHistory {
  position: Position
  user?: string
  userId?: string
  timestamp: number
}

export interface VersionHistory {
  version: string
  timestamp: number
}

export interface ConfigHistory {
  attribute: string
  from: string
  to: string
  user: string
  timestamp: number
}

export interface AssociationHistory {
  withType: string
  link: LINK // add or remove
  name: string // name at the time
  id: string // to hyperlink name
  user: string // sk of user
  timestamp: number
}

export interface ErrorHistory {
  error: string
  startTimestamp: number
  finishedTime?: number
  user?: string
  state: string
}

export interface StateHistory {
  state: DeviceState
  reason: string
  user: ValidBusinessObject
  time: number
}

type potentialTypes = NetworkAS | LoctionHistory | VersionHistory | ConfigHistory | AssociationHistory | ErrorHistory | DeviceHistoryBase

export type DeviceHistoryBase = MapLike<any>

export type DeviceHistoryProps = MayContainMetaData & {
  messageType?: number
} & {
  initial?: DeviceInitialType
  NAS?: NetworkAS[]
  location?: LoctionHistory[]
  version?: VersionHistory[]
  config?: ConfigHistory[]
  associations?: AssociationHistory[]
  // would have the most data. Need to be careful about adding new rows when the previous has not been resolved
  errors?: ErrorHistory[]
  history?: DeviceHistoryBase[] // remove later only for debugging purposes
}

export interface IDeviceHistory<DC extends DeviceHistoryBase> extends DeviceHistoryProps, DatastoreObjectType {
  metadata?: dbObjectMeta
  initial?: DeviceInitialType
  NAS?: NetworkAS[]
  location?: LoctionHistory[]
  version?: VersionHistory[]
  config?: ConfigHistory[]
  associations?: AssociationHistory[]
  // would have the most data. Need to be careful about adding new rows when the previous has not been resolved
  errors?: ErrorHistory[]
}

const HISTORY_LIMIT = 15

/**
 * @class Holds configuration object retrieved from device via downlink request
 * This is a pseudo property ie an instance should have pk as the sk of the device it belongs to,
 * And since it can only have one config, the instances sk should be a concaentation of pk + className
 */
export class DeviceHistory<DC extends DeviceHistoryBase>
  extends ModelCommon
  implements IDeviceHistory<DC>, TypeScopedValidCoreApplicationType<'DeviceHistory'>
{
  type: typeof className = 'DeviceHistory'
  metadata?: dbObjectMeta

  constructor(ob: Partial<DeviceHistoryProps> & DatastoreObjectType & { name?: string; id: string }) {
    super(className, { ...ob })
    this.history = ob.history
    this.metadata = ob.metadata // 20204 09 fix for overwriting property in ModelCommon
  }
  initial?: DeviceInitialType | undefined
  NAS?: NetworkAS[] | undefined
  location?: LoctionHistory[] | undefined
  version?: VersionHistory[] | undefined
  config?: ConfigHistory[] | undefined
  associations?: AssociationHistory[] | undefined
  errors?: ErrorHistory[] | undefined
  messageType?: number | undefined
  state?: StateHistory[]
  history: MapLike<any>[] | undefined = []

  @enumerable(false)
  update = (reported?: MapLike<any>) => {
    if (reported) {
      let meta = reported.meta
      let currentTime = meta.timestamp ?? new Date().getMilliseconds()
      // is it first time
      if (!this.initial) {
        // battery is not in the first
        this.initial = {
          RSSI: meta.rssi,
          SNR: meta.snr,
          // battery: 1,
          gateway: meta.nasSourceIp,
          timestamp: currentTime,
          NS: meta.networkKey,
        }
      }

      // only set once
      if (meta.battery && this.initial.battery === undefined) {
        this.initial.battery = meta.battery
      }

      if (meta.networkKey) {
        let NASInput = {
          provider: meta.networkKey,
          timestamp: currentTime,
        }
        if (!this.NAS) {
          this.NAS = [NASInput]
        } else {
          let last = this.NAS[0].provider
          if (last !== meta.networkKey) {
            this.NAS.unshift(NASInput)
          }
        }
      }

      if (meta.version) {
        let versionInput = {
          version: meta.version,
          timestamp: currentTime,
        }
        if (!this.version) {
          this.version = [versionInput]
        } else {
          let last = this.version[0].version
          if (last !== meta.networkKey) {
            this.version.unshift(versionInput)
          }
        }
      }

      if (reported.error) {
        let combined = Object.keys(reported.error)
          .reduce((res, cur) => {
            if (reported.error[cur] === 1) {
              res.push(cur)
            }
            return res
          }, [] as string[])
          .join(', ')

        let errorInput = {
          error: combined,
          startTimestamp: currentTime,
          state: 'Unresolved',
        }
        if (!this.errors) {
          this.errors = [errorInput]
        } else {
          let last = this.errors[0].error
          if (last !== combined) {
            this.errors.unshift(errorInput)
          }
        }
      }

      // messageType
      // history

      let hist = this.history ?? []
      if (hist.length >= HISTORY_LIMIT) {
        hist.splice(HISTORY_LIMIT - 1)
      }
      hist.unshift(reported)
      this.history = hist
    }
  }

  // appendConfig(data: ConfigHistory) {
  //   if (this.config) {
  //     this.config.push(data);
  //   } else {
  //     this.config = [data];
  //   }
  // }

  /**
   * Make sure the entity is saved!
   * @param error Error of this device
   * @param user User account made this change
   */
  appendFault(error: string, user?: ValidModelObject<'User'>) {
    let fault: ErrorHistory = {
      error,
      startTimestamp: Date.now(),
      state: 'Unresolved',
    }
    if (user) {
      fault = { ...fault, user: user.name }
    }

    if (this.errors) {
      this.errors.push(fault)
    } else {
      this.errors = [fault]
    }
  }

  /**
   * Make sure the entity is saved!
   * @param version
   */
  appendVersion(version: string | number) {
    let newVersion: VersionHistory = {
      version: typeof version === 'string' ? version : version.toString(),
      timestamp: Date.now(),
    }
    if (this.version && this.version.length > 0) {
      // Shouldn't be required, but confirm the last version is different
      if (this.version[this.version.length - 1].version !== newVersion.version) {
        this.version.push(newVersion)
      }
    } else {
      this.version = [newVersion]
    }
  }

  /**
   * Make sure the entity is saved!
   * @param position Position of the new location
   * @param user User making this change
   */
  appendLocation(position: Position, user?: ValidModelObject<'User'>) {
    let location: LoctionHistory = {
      position,
      timestamp: Date.now(),
    }

    if (user) {
      location = { ...location, user: user.name, userId: user.id }
    }

    if (this.location) {
      this.location.push(location)
    } else {
      this.location = [location]
    }
  }

  /**
   * Make sure the entity is saved!
   * @param networking Network of where it is now checking in it
   */
  appendNetwork(networking: Networks) {
    let network = {
      provider: networking,
      timestamp: Date.now(),
    }
    if (this.NAS && this.NAS.length > 0) {
      // Shouldn't be required, but confirm the last network is different
      if (this.NAS[this.NAS.length - 1].provider.toString() !== networking.toString()) {
        this.NAS.push(network)
      }
    } else {
      this.NAS = [network]
    }
  }

  /**
   * Make sure the entity is saved!
   * Updates the object with the entity.
   * @param data Data of the entity being linked to the device
   * @param user The user doing this action
   * @param link Linking action, default to linking
   */
  appendAssociation(data: ValidBusinessObject, user: ValidModelObject<'User'>, link: LINK = LINK.LINK) {
    // create it
    let association = {
      withType: data.type,
      link,
      name: data.name,
      id: data.pk, // to hyperlink name
      user: user.sk, // sk of user
      timestamp: Date.now(),
    }

    if (this.associations) {
      this.associations.push(association)
    } else {
      this.associations = [association]
    }
  }

  /**
   * Make sure the entity is saved!
   * Generic linking method that may potential match the wrong type
   * Allows slight variations of the format to be saved
   * @param data
   * @param type
   */
  appendGeneric(data: potentialTypes, type: string) {
    if (this[type]) {
      this[type].push(data)
    } else {
      this[type] = [data]
    }
  }

  static createForDevice(device: ValidModelObject<'Device'>) {
    const sk = `${device.sk}_${className}`
    const pk = device.pk
    const name = `${device.name ?? device.sk} DeviceHistory`
    const id = `${device.id}_${className}`

    return new DeviceHistory({ sk, pk, name, id })
  }
}
